import { Box, Divider, Flex, Skeleton, Text, VStack } from '@chakra-ui/react';
import { ProductSkeleton } from '@getjust/leaf';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { InboundFormMessage } from '$src/models/state';

import { SummaryRowSkeleton } from './SummaryRowSkeleton';

interface DesktopCartSkeletonProps {
  lineItems: InboundFormMessage['cart']['lineItems'];
  maxVisibleItems: number;
  inverseOrder?: boolean;
  loadingColor?: string;
}

export const DesktopCartSkeleton: React.FC<DesktopCartSkeletonProps> = ({
  lineItems,
  maxVisibleItems,
  inverseOrder = false,
  loadingColor = 'var(--bg-secondary, #F7F7F7)',
}) => {
  const { t } = useTranslation(['common']);

  return (
    <VStack spacing={3} align="stretch" flex="1">
      <Box fontSize="sm" order={inverseOrder ? 3 : undefined}>
        <SummaryRowSkeleton
          labelKey="commons.subtotal"
          aria-label={t('aria.subtotal_section')}
          loadingColor={loadingColor}
        />
        <SummaryRowSkeleton
          labelKey="commons.shipping_method"
          aria-label={t('aria.shipping_method_section')}
          loadingColor={loadingColor}
        />
      </Box>

      <Box order={inverseOrder ? 4 : undefined}>
        <Flex justifyContent="space-between" alignItems="center" aria-label={t('aria.total_section')}>
          <Text color="var(--text, #1C1C1C)" fontSize="md" fontWeight="semibold" lineHeight="6">
            {t('commons.total')}
          </Text>
          <Skeleton height="1rem" width="3rem" startColor={loadingColor} endColor="var(--border, #E0E0E0)" />
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Text color="var(--text-muted, #8F8F8F)" fontSize="0.625rem">
            ({t('commons.taxIncludedTitle')})
          </Text>
        </Flex>
      </Box>

      <Divider order={inverseOrder ? 2 : undefined} />

      <Box order={inverseOrder ? 1 : undefined}>
        <VStack spacing={3} align="stretch">
          {lineItems && lineItems?.length > 0
            ? lineItems
                ?.slice(0, maxVisibleItems)
                .map((_, index) => <ProductSkeleton key={index} loadingColor={loadingColor} />)
            : [<ProductSkeleton key="fallback" loadingColor={loadingColor} />]}
          {lineItems && lineItems.length > maxVisibleItems && (
            <Text fontSize="sm" color="var(--text-secondary, #585858)" textAlign="center" py={2}>
              +{lineItems.length - maxVisibleItems} {t('commons.more_items')}
            </Text>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};
